import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { HeroIllustration } from "@/components/hero/HeroIllustration";
import { JoinExamCard } from "@/components/landing/join-exam-card";
import { SocialProof } from "@/components/landing/social-proof";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { ArrowRight, Brain, ClipboardList, LineChart, Sparkles, Trophy, Zap } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Subiza — Online exams that run themselves" },
      {
        name: "description",
        content:
          "Create and grade online exams in minutes. Generate questions with AI, students join with a PIN, and results are ready the moment the exam ends.",
      },
      { property: "og:title", content: "Subiza — Online exams that run themselves" },
      {
        property: "og:description",
        content:
          "Generate questions with AI, host a live exam with a PIN, and get marked results instantly.",
      },
    ],
  }),
  component: LandingPage,
});

const FEATURES = [
  {
    icon: Brain,
    title: "AI question generation",
    desc: "Paste notes or upload a document and Subiza drafts multiple choice, true or false, fill in the blank and matching questions.",
  },
  {
    icon: Zap,
    title: "Live exams with a PIN",
    desc: "Students join from any device with a six-digit PIN. There is no student account to create.",
  },
  {
    icon: LineChart,
    title: "Auto-grading and results",
    desc: "Objective questions are marked the moment a student submits, with scores and per-question statistics.",
  },
  {
    icon: ClipboardList,
    title: "See what to revisit",
    desc: "Find the questions the class missed most and the topics worth going over again next lesson.",
  },
];

const STEPS = [
  {
    n: "01",
    t: "Create an exam",
    d: "Write questions by hand, or generate a draft from your own notes and then review every question before publishing.",
  },
  {
    n: "02",
    t: "Students join with a PIN",
    d: "Share the six-digit PIN. Students open Subiza on any device and sit the exam live, with a timer and no sign-up.",
  },
  {
    n: "03",
    t: "Review the results",
    d: "Scores and per-question statistics are ready as soon as the exam ends, so you can mark and give feedback straight away.",
  },
];

function LandingPage() {
  const revealScope = useRef(null);
  useScrollReveal(revealScope);

  return (
    <div ref={revealScope} className="min-h-screen bg-bg">
      <Header />

      {/* Hero: tagline and CTAs on the left, illustration on the right */}
      <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 pt-4 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:pt-8 lg:pb-24">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs font-medium">
            <Sparkles className="size-3.5 text-accent" aria-hidden="true" />
            AI question generation · Live exams · Auto-grading
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-balance sm:text-5xl lg:text-7xl">
            Quizzes that <span className="text-gradient-primary">turn classrooms</span> into game
            shows.
          </h1>
          <p className="max-w-xl text-lg text-muted">
            Create quizzes by hand or in seconds with AI. Host live, share a PIN, and watch students
            compete in real time.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button size="lg" asChild className="text-base bg-gradient-primary shadow-elegant">
              <Link to="/auth">
                Start hosting <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#join">I&rsquo;m a student</a>
            </Button>
          </div>
        </div>

        <div className="relative">
          <HeroIllustration />
        </div>
      </section>

      {/* Join an exam (target of the "I'm a student" CTA) */}
      <section id="join" className="mx-auto max-w-7xl scroll-mt-20 px-4 pb-16 sm:px-6 lg:pb-24">
        <JoinExamCard />
      </section>

      {/* Key features */}
      <section
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24"
        aria-labelledby="features-heading"
      >
        <div className="mx-auto mb-12 max-w-2xl text-center" data-reveal>
          <h2 id="features-heading" className="text-3xl font-bold tracking-tight lg:text-4xl">
            Everything you need to run an online exam
          </h2>
          <p className="mt-3 text-muted">
            From generating questions to marking them, Subiza keeps the whole exam in one place.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Card
              key={f.title}
              data-reveal
              data-reveal-delay={i * 0.06}
              className="rounded-2xl border-2 bg-gradient-card p-6 transition-all hover:-translate-y-1 hover:shadow-elegant"
            >
              <div className="grid size-11 place-items-center rounded-xl bg-gradient-primary text-accent-foreground shadow-soft">
                <f.icon className="size-5" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24"
        aria-labelledby="how-it-works-heading"
      >
        <div className="mx-auto mb-12 max-w-2xl text-center" data-reveal>
          <h2 id="how-it-works-heading" className="text-3xl font-bold tracking-tight lg:text-4xl">
            How it works
          </h2>
          <p className="mt-3 text-muted">Three steps from an idea to a marked set of results.</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <Card
              key={s.n}
              data-reveal
              data-reveal-delay={i * 0.06}
              className="relative overflow-hidden rounded-2xl border-2 p-8"
            >
              <div
                className="absolute -top-2 right-2 font-display text-7xl font-black text-accent/10"
                aria-hidden="true"
              >
                {s.n}
              </div>
              <Trophy className="size-6 text-accent" aria-hidden="true" />
              <h3 className="mt-4 text-xl font-bold">{s.t}</h3>
              <p className="mt-1 text-sm text-muted">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Trust / social proof */}
      <SocialProof />

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20 lg:pb-28">
        <Card
          data-reveal
          className="rounded-2xl border-0 bg-gradient-primary p-10 text-center text-accent-foreground shadow-elegant lg:p-14"
        >
          <h2 className="text-3xl font-bold tracking-tight lg:text-4xl">
            Ready to run your first exam?
          </h2>
          <p className="mt-2 opacity-90">Set up a teacher account in under a minute.</p>
          <Button size="lg" variant="secondary" asChild className="mt-6">
            <Link to="/auth">
              Create teacher account <ArrowRight className="size-4" />
            </Link>
          </Button>
        </Card>
      </section>
    </div>
  );
}
